import React, { useState } from 'react';
import { SEO } from '../components/seo/SEO';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/Button';
import { Calendar, Clock, CheckCircle2, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';
import { EMAIL_CONFIG, isEmailConfigured } from '../services/emailService';
import styles from './ConsultationPage.module.css';

export const ConsultationPage: React.FC = () => {
    const { language } = useLanguage();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        preferredTime: '',
        duration: '30',
        topic: '',
        message: '',
        website: '', // Honeypot field - bots fill this, humans don't see it
        consent: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const content = {
        de: {
            badge: 'KOSTENLOSE BERATUNG',
            title: 'Buchen Sie Ihr kostenloses Strategiegespräch',
            subtitle: 'Wählen Sie die Dauer, die am besten zu Ihnen passt. Wir analysieren gemeinsam Ihre Herausforderungen und zeigen Ihnen, wie KI und Automatisierung Ihr Unternehmen transformieren können.',
            benefits: [
                'Individuelle Analyse Ihrer aktuellen Prozesse',
                'Konkrete Empfehlungen für Quick Wins',
                'Roadmap für Ihre digitale Transformation',
                'Keine Verpflichtungen – 100% kostenlos'
            ],
            form: {
                name: 'Name',
                email: 'E-Mail',
                company: 'Unternehmen',
                phone: 'Telefon',
                preferredTime: 'Bevorzugte Zeit',
                duration: 'Gesprächsdauer',
                durations: {
                    '30': '30 Minuten – Schnelle Beratung',
                    '60': '60 Minuten – Detaillierte Analyse',
                    '90': '90 Minuten – Umfassende Strategiesession'
                },
                topic: 'Thema',
                topics: {
                    ai: 'KI & Automatisierung',
                    erp: 'ERP & SAP',
                    software: 'Softwareentwicklung',
                    other: 'Sonstiges'
                },
                message: 'Ihre Nachricht (optional)',
                submit: 'Kostenlos Beratung anfragen',
                submitting: 'Wird gesendet...',
                consentLabel: 'Ich stimme der Verarbeitung meiner Daten gemäß der',
                consentLink: 'Datenschutzerklärung',
                consentLabelSuffix: 'zu.',
                consentRequired: 'Bitte bestätigen Sie die Datenschutzerklärung.',
                workEmailError: 'Bitte geschäftliche E-Mail-Adresse verwenden.',
                times: {
                    morning: 'Vormittags (9-12 Uhr)',
                    afternoon: 'Nachmittags (13-17 Uhr)',
                    evening: 'Abends (17-19 Uhr)'
                }
            },
            success: {
                title: 'Vielen Dank!',
                message: 'Wir haben Ihre Anfrage erhalten und melden uns innerhalb von 24 Stunden bei Ihnen.'
            },
            error: {
                title: 'Fehler beim Senden',
                message: 'Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt unter info@integrovaai.de',
                retry: 'Erneut versuchen'
            },
            chooseDuration: 'Wählen Sie Ihre Dauer'
        },
        en: {
            badge: 'FREE CONSULTATION',
            title: 'Book Your Free Strategy Call',
            subtitle: 'Choose the duration that fits you best. We\'ll analyze your challenges together and show you how AI and automation can transform your business.',
            benefits: [
                'Individual analysis of your current processes',
                'Concrete recommendations for quick wins',
                'Roadmap for your digital transformation',
                'No obligations – 100% free'
            ],
            form: {
                name: 'Name',
                email: 'Email',
                company: 'Company',
                phone: 'Phone',
                preferredTime: 'Preferred Time',
                duration: 'Session Duration',
                durations: {
                    '30': '30 Minutes – Quick Consultation',
                    '60': '60 Minutes – Detailed Analysis',
                    '90': '90 Minutes – Comprehensive Strategy Session'
                },
                topic: 'Topic',
                topics: {
                    ai: 'AI & Automation',
                    erp: 'ERP & SAP',
                    software: 'Software Development',
                    other: 'Other'
                },
                message: 'Your Message (optional)',
                submit: 'Request Free Consultation',
                submitting: 'Sending...',
                consentLabel: 'I agree to the processing of my data in accordance with the',
                consentLink: 'Privacy Policy',
                consentLabelSuffix: '.',
                consentRequired: 'Please confirm the privacy policy.',
                workEmailError: 'Please use a business email address.',
                times: {
                    morning: 'Morning (9-12)',
                    afternoon: 'Afternoon (1-5 PM)',
                    evening: 'Evening (5-7 PM)'
                }
            },
            success: {
                title: 'Thank You!',
                message: 'We\'ve received your request and will get back to you within 24 hours.'
            },
            error: {
                title: 'Error Sending',
                message: 'Please try again or contact us directly at info@integrovaai.de',
                retry: 'Try Again'
            },
            chooseDuration: 'Choose Your Duration'
        }
    };

    const t = content[language];

    const getTopicLabel = (topicKey: string): string => {
        const topics = t.form.topics as Record<string, string>;
        return topics[topicKey] || topicKey;
    };

    const getTimeLabel = (timeKey: string): string => {
        const times = t.form.times as Record<string, string>;
        return times[timeKey] || timeKey;
    };

    const getDurationLabel = (durationKey: string): string => {
        const durations = t.form.durations as Record<string, string>;
        return durations[durationKey] || `${durationKey} Minutes`;
    };



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Honeypot spam check - if this field is filled, it's a bot
        if (formData.website) {
            console.log('Bot detected via honeypot');
            // Fake success to not alert the bot
            setSubmitStatus('success');
            return;
        }

        if (!formData.consent) {
            setSubmitStatus('error');
            setErrorMessage(t.form.consentRequired);
            return;
        }



        setIsSubmitting(true);
        setErrorMessage('');

        // Check if EmailJS is configured
        if (!isEmailConfigured()) {
            console.warn('EmailJS not configured. Form data:', formData);
            setSubmitStatus('error');
            setErrorMessage(t.error.message);
            setIsSubmitting(false);
            return;
        }

        const templateParams = {
            from_name: formData.name,
            from_email: formData.email,
            company: formData.company || 'Not provided',
            phone: formData.phone || 'Not provided',
            subject: `Consultation Request: ${getTopicLabel(formData.topic)}`,
            topic: getTopicLabel(formData.topic),
            duration: getDurationLabel(formData.duration),
            preferred_time: formData.preferredTime ? getTimeLabel(formData.preferredTime) : 'Not specified',
            message: formData.message || 'No additional message',
            consent: formData.consent ? 'yes' : 'no',
            consent_timestamp: new Date().toISOString(),
            to_email: 'info@integrovaai.de',
        };

        try {
            await emailjs.send(
                EMAIL_CONFIG.SERVICE_ID,
                EMAIL_CONFIG.TEMPLATE_ID_CONSULTATION,
                templateParams,
                EMAIL_CONFIG.PUBLIC_KEY
            );
            setSubmitStatus('success');
        } catch (error) {
            console.error('EmailJS Error:', error);
            setSubmitStatus('error');
            setErrorMessage(t.error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const target = e.target as HTMLInputElement;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        setFormData({ ...formData, [target.name]: value });
    };

    const handleRetry = () => {
        setSubmitStatus('idle');
        setErrorMessage('');
    };

    const handleBookCalendly = () => {
        window.open('https://calendly.com/integrovaai-info/30min', '_blank', 'noopener,noreferrer');
    };

    // Success State
    if (submitStatus === 'success') {
        return (
            <div className={styles.page}>
                <div className={styles.successContainer}>
                    <motion.div
                        className={styles.successCard}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.15 }}
                    >
                        <CheckCircle2 size={64} className={styles.successIcon} />
                        <h2>{t.success.title}</h2>
                        <p>{t.success.message}</p>
                    </motion.div>
                </div>
            </div>
        );
    }

    // Error State
    if (submitStatus === 'error') {
        return (
            <div className={styles.page}>
                <div className={styles.successContainer}>
                    <motion.div
                        className={styles.successCard}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.15 }}
                    >
                        <AlertCircle size={64} className={styles.errorIcon} />
                        <h2>{t.error.title}</h2>
                        <p>{t.error.message}</p>
                        {errorMessage && <p className={styles.errorDetail}>{errorMessage}</p>}
                        <Button variant="primary" size="lg" onClick={handleRetry}>
                            {t.error.retry}
                        </Button>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <SEO
                title="Free Consultation"
                description="Book a free strategy call with Integrova AI to discuss your digital transformation needs."
                canonical="/consultation"
            />
            <section className={styles.hero}>
                <div className={styles.hero__container}>
                    <motion.span
                        className={styles.hero__badge}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15 }}
                    >
                        <Calendar size={16} />
                        {t.badge}
                    </motion.span>
                    <motion.h1
                        className={styles.hero__title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15, delay: 0 }}
                    >
                        {t.title}
                    </motion.h1>
                    <motion.p
                        className={styles.hero__subtitle}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15, delay: 0 }}
                    >
                        {t.subtitle}
                    </motion.p>
                    <motion.div
                        className={styles.hero__ctaAlt}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15, delay: 0.05 }}
                    >
                        <Button variant="secondary" size="lg" onClick={handleBookCalendly}>
                            {language === 'de' ? 'Alternativ: Direkt über Calendly buchen' : 'Or book via Calendly'}
                        </Button>
                    </motion.div>
                </div>
            </section>

            <section className={styles.content}>
                <div className={styles.container}>
                    <div className={styles.grid}>
                        {/* Benefits */}
                        <motion.div
                            className={styles.benefits}
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.15, delay: 0 }}
                        >
                            <div className={styles.benefitsCard}>
                                <Clock size={32} className={styles.benefitsIcon} />
                                <h3>{t.chooseDuration}</h3>
                                <div className={styles.durationOptions}>
                                    {['30', '60', '90'].map((dur) => (
                                        <button
                                            key={dur}
                                            type="button"
                                            className={`${styles.durationOption} ${formData.duration === dur ? styles.durationOptionActive : ''}`}
                                            onClick={() => setFormData({ ...formData, duration: dur })}
                                        >
                                            {dur} {language === 'de' ? 'Min' : 'Min'}
                                        </button>
                                    ))}
                                </div>
                                <ul className={styles.benefitsList}>
                                    {t.benefits.map((benefit, index) => (
                                        <li key={index}>
                                            <CheckCircle2 size={18} />
                                            <span>{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>

                        {/* Form */}
                        <motion.form
                            className={styles.form}
                            onSubmit={handleSubmit}
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.15, delay: 0 }}
                        >
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="name">{t.form.name} *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="email">{t.form.email} *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="company">{t.form.company}</label>
                                    <input
                                        type="text"
                                        id="company"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleChange}
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="phone">{t.form.phone}</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="duration">{t.form.duration} *</label>
                                    <select
                                        id="duration"
                                        name="duration"
                                        value={formData.duration}
                                        onChange={handleChange}
                                        required
                                        disabled={isSubmitting}
                                    >
                                        <option value="30">{t.form.durations['30']}</option>
                                        <option value="60">{t.form.durations['60']}</option>
                                        <option value="90">{t.form.durations['90']}</option>
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="topic">{t.form.topic} *</label>
                                    <select
                                        id="topic"
                                        name="topic"
                                        value={formData.topic}
                                        onChange={handleChange}
                                        required
                                        disabled={isSubmitting}
                                    >
                                        <option value="">--</option>
                                        <option value="ai">{t.form.topics.ai}</option>
                                        <option value="erp">{t.form.topics.erp}</option>
                                        <option value="software">{t.form.topics.software}</option>
                                        <option value="other">{t.form.topics.other}</option>
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="preferredTime">{t.form.preferredTime}</label>
                                    <select
                                        id="preferredTime"
                                        name="preferredTime"
                                        value={formData.preferredTime}
                                        onChange={handleChange}
                                        disabled={isSubmitting}
                                    >
                                        <option value="">--</option>
                                        <option value="morning">{t.form.times.morning}</option>
                                        <option value="afternoon">{t.form.times.afternoon}</option>
                                        <option value="evening">{t.form.times.evening}</option>
                                    </select>
                                </div>
                            </div>
                            {/* Honeypot field - hidden from humans, bots fill it */}
                            <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }} aria-hidden="true">
                                <label htmlFor="website">Website</label>
                                <input
                                    type="text"
                                    id="website"
                                    name="website"
                                    value={formData.website}
                                    onChange={handleChange}
                                    tabIndex={-1}
                                    autoComplete="off"
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="message">{t.form.message}</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows={4}
                                    value={formData.message}
                                    onChange={handleChange}
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className={styles.formConsent}>
                                <label className={styles.formConsentLabel}>
                                    <input
                                        type="checkbox"
                                        name="consent"
                                        checked={formData.consent}
                                        onChange={handleChange}
                                        className={styles.formConsentCheckbox}
                                        disabled={isSubmitting}
                                        required
                                    />
                                    <span>
                                        {t.form.consentLabel}{' '}
                                        <Link to="/datenschutz" className={styles.formConsentLink}>
                                            {t.form.consentLink}
                                        </Link>{' '}
                                        {t.form.consentLabelSuffix}
                                    </span>
                                </label>
                            </div>
                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                fullWidth
                                icon={isSubmitting ? <Loader2 size={20} className={styles.spinner} /> : <ArrowRight size={20} />}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? t.form.submitting : (
                                    language === 'en' ? (
                                        <>Request <strong style={{ fontWeight: 700 }}>Free</strong> Consultation</>
                                    ) : (
                                        <><strong style={{ fontWeight: 700 }}>Kostenlos</strong> Beratung anfragen</>
                                    )
                                )}
                            </Button>
                        </motion.form>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ConsultationPage;
